import type { UserViewsResponse } from '@/types/saved-view'

/**
 * Returns the IDs of all currently-visible views (default + user + shared + public)
 * from a UserViewsResponse, preserving the existing semantics where a view is
 * considered visible unless `isVisible === false`. Used when building save-view
 * payloads so the server can reconcile per-user view visibility.
 *
 * Pure / side-effect-free; safe to call from hooks or components.
 */
export function buildVisibleViewIds(viewsResponse: UserViewsResponse | null | undefined): string[] {
  if (!viewsResponse) return []

  const ids: string[] = []

  if (viewsResponse.defaultView && viewsResponse.defaultView.isVisible !== false) {
    ids.push(viewsResponse.defaultView.viewSettingsId)
  }
  viewsResponse.userViews?.forEach(v => {
    if (v.isVisible !== false) ids.push(v.viewSettingsId)
  })
  viewsResponse.sharedViews?.forEach(v => {
    if (v.isVisible !== false) ids.push(v.viewSettingsId)
  })
  viewsResponse.publicViews?.forEach(v => {
    if (v.isVisible !== false) ids.push(v.viewSettingsId)
  })

  return ids
}

/**
 * Serializes a stageFilter's `values` to a JSON string when present, leaving
 * other fields untouched. Matches the wire format the save endpoints expect.
 */
export function serializeStageFilters<T extends { values?: any }>(filters: T[]): T[] {
  return filters.map(filter => ({
    ...filter,
    // Nullish check (not truthy) so legitimate falsy values like 0/false survive serialization.
    values: filter.values !== undefined && filter.values !== null
      ? JSON.stringify(filter.values)
      : undefined,
  }))
}

/**
 * Serializes a globalFilter's `values` to a JSON string. Tolerant of values
 * that are already strings (already-serialized) — leaves them as-is.
 */
export function serializeGlobalFilters(
  filters: { fieldConfigurationId: string; filter: string; values?: any }[]
): { fieldConfigurationId: string; filter: string; values?: string }[] {
  return filters.map(filter => ({
    fieldConfigurationId: filter.fieldConfigurationId,
    filter: filter.filter,
    // Nullish check (not truthy) so legitimate falsy values like 0/false survive serialization.
    values:
      typeof filter.values === 'string'
        ? filter.values
        : filter.values !== undefined && filter.values !== null
          ? JSON.stringify(filter.values)
          : undefined,
  }))
}
